import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../firestore.js";
import { auth } from "../auth.js";
import { renderPendingApprovals } from "./pending-approvals.js";

const KENGURS_GROUPS = [
  { key: "2", label: "2. kl.", grades: ["2"] },
  { key: "3-4", label: "3.–4. kl.", grades: ["3", "4"] },
  { key: "5-6", label: "5.–6. kl.", grades: ["5", "6"] },
  { key: "7-8", label: "7.–8. kl.", grades: ["7", "8"] },
  { key: "9-10", label: "9.–10. kl.", grades: ["9", "10"] },
  { key: "11-12", label: "11.–12. kl.", grades: ["11", "12", "11-12"] },
];

export async function renderDashboard(root) {
  const uid = auth.currentUser?.uid;

  root.innerHTML = `
    <div id="pending"></div>
    <div class="dashboard-greeting"><h2>Ielādē...</h2></div>
  `;

  await renderPendingApprovals(root.querySelector("#pending"));
  if (!root.isConnected || auth.currentUser?.uid !== uid) return;

  const [allTests, attempts, userSnap] = await Promise.all([
    fetchAllTests(),
    uid ? fetchAttempts(uid) : [],
    uid ? getDoc(doc(db, `users/${uid}`)) : null,
  ]);
  if (!root.isConnected || auth.currentUser?.uid !== uid) return;

  const prefs = userSnap?.data()?.preferences ?? {};
  let selectedGroup = prefs.ageGroup || "3-4";
  if (!KENGURS_GROUPS.find((g) => g.key === selectedGroup)) {
    selectedGroup = "3-4";
  }

  const attemptsByTest = groupAttempts(attempts);
  const stats = computeStats(attemptsByTest);
  const userName = userSnap?.data()?.name;
  const greeting = userName ? `Sveiks, ${userName}!` : "Sveiki!";

  root.innerHTML = `
    <div id="pending"></div>

    <div class="dashboard-greeting">
      <h2>${greeting}</h2>
    </div>

    <div class="dashboard-stats">
      <div class="stat-card">
        <div class="stat-value">${stats.testsCompleted}</div>
        <div class="stat-label">Izpildīti testi</div>
      </div>
      <div class="stat-card green">
        <div class="stat-value">${stats.avgPercent}%</div>
        <div class="stat-label">Vidējais rezultāts</div>
      </div>
    </div>

    <hr class="dashboard-divider">

    <div class="section-label">Sacensība</div>
    <div class="test-type-row">
      <button class="test-type-btn active" data-type="kengurs">🦘 Kengurs</button>
      <span class="test-type-btn placeholder">📐 Drīzumā...</span>
    </div>

    <div class="section-label">Vecuma grupa</div>
    <div class="age-group-row" id="age-group-row">
      ${KENGURS_GROUPS.map(
    (g) =>
      `<button class="age-group-pill${g.key === selectedGroup ? " active" : ""}" data-group="${g.key}">${g.label}</button>`,
  ).join("")}
    </div>

    <div class="section-label">Testi</div>
    <div class="test-list" id="test-list"></div>
  `;

  await renderPendingApprovals(root.querySelector("#pending"));

  const ageGroupRow = root.querySelector("#age-group-row");
  const testList = root.querySelector("#test-list");

  function renderTests() {
    const group = KENGURS_GROUPS.find((g) => g.key === selectedGroup);
    if (!group) return;

    ageGroupRow.querySelectorAll(".age-group-pill").forEach((btn) => {
      btn.classList.toggle("active", btn.dataset.group === selectedGroup);
    });

    const filtered = allTests
      .filter((t) => group.grades.includes(t.grade))
      .sort((a, b) => {
        if (b.year !== a.year) return b.year.localeCompare(a.year);
        return a.grade.localeCompare(b.grade);
      });

    if (filtered.length === 0) {
      testList.innerHTML = `<p class="no-tests-message">Šajā vecuma grupā šobrīd nav pieejamu testu.</p>`;
      return;
    }

    testList.innerHTML = filtered
      .map((t) => {
        const key = `${t.year}:${t.grade}`;
        const ta = attemptsByTest[key];
        let scoreHtml, metaHtml;

        if (ta) {
          scoreHtml = `<div class="test-row-score">✓ Labākais: ${ta.bestCorrect}/${ta.total}</div>`;
          const attemptsWord = ta.count === 1 ? "mēģinājums" : "mēģinājumi";
          metaHtml = `<div class="test-row-meta">${ta.count} ${attemptsWord} · ${formatRelative(ta.lastDate)}</div>`;
        } else {
          scoreHtml = `<div class="test-row-score not-attempted">Nav izpildīts</div>`;
          metaHtml = "";
        }

        return `
          <div class="test-row">
            <div class="test-row-year">
              <span class="year">${t.year}</span>
              <span class="grade">${group.label}</span>
            </div>
            <div class="test-row-info">
              ${scoreHtml}
                ${metaHtml}
            </div>
            <button class="test-row-start" data-year="${t.year}" data-grade="${t.grade}">Sākt</button>
          </div>
        `;
      })
      .join("");
  }

  ageGroupRow.addEventListener("click", (e) => {
    const pill = e.target.closest(".age-group-pill");
    if (!pill) return;
    selectedGroup = pill.dataset.group;
    renderTests();
    if (uid) {
      updateDoc(doc(db, `users/${uid}`), {
        "preferences.ageGroup": selectedGroup,
      }).catch(() => { });
    }
  });

  testList.addEventListener("click", (e) => {
    const btn = e.target.closest(".test-row-start");
    if (!btn) return;
    location.hash = `#/attempt/${btn.dataset.year}/${btn.dataset.grade}`;
  });

  renderTests();
}

async function fetchAllTests() {
  const snap = await getDocs(collection(db, "answerKeys"));
  const years = snap.docs.map((d) => d.id);
  const tests = [];
  await Promise.all(
    years.map(async (year) => {
      const gradesSnap = await getDocs(
        collection(db, `answerKeys/${year}/grades`),
      );
      gradesSnap.docs.forEach((d) => tests.push({ year, grade: d.id }));
    }),
  );
  return tests;
}

async function fetchAttempts(uid) {
  const snap = await getDocs(
    query(
      collection(db, `competitions/${uid}/attempts`),
      orderBy("submittedAt", "desc"),
    ),
  );
  return snap.docs.map((d) => d.data());
}

function groupAttempts(attempts) {
  const map = {};
  for (const a of attempts) {
    const key = `${a.year}:${a.grade}`;
    const total = a.correct + a.wrong + a.blank;
    const date = a.submittedAt?.toDate ? a.submittedAt.toDate() : null;

    if (!map[key]) {
      map[key] = { bestCorrect: a.correct, total, count: 1, lastDate: date };
    } else {
      map[key].count++;
      if (a.correct > map[key].bestCorrect) {
        map[key].bestCorrect = a.correct;
        map[key].total = total;
      }
      if (date && (!map[key].lastDate || date > map[key].lastDate)) {
        map[key].lastDate = date;
      }
    }
  }
  return map;
}

function computeStats(attemptsByTest) {
  const keys = Object.keys(attemptsByTest);
  const testsCompleted = keys.length;
  if (testsCompleted === 0) return { testsCompleted: 0, avgPercent: 0 };

  const totalPercent = keys.reduce((sum, k) => {
    const t = attemptsByTest[k];
    return sum + (t.total > 0 ? (t.bestCorrect / t.total) * 100 : 0);
  }, 0);

  return {
    testsCompleted,
    avgPercent: Math.round(totalPercent / testsCompleted),
  };
}

function formatRelative(date) {
  if (!date) return "";
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "šodien";
  if (diffDays === 1) return "vakar";
  if (diffDays < 7) return `pirms ${diffDays} dienām`;
  const weeks = Math.floor(diffDays / 7);
  if (weeks === 1) return "pirms 1 nedēļas";
  if (weeks < 5) return `pirms ${weeks} nedēļām`;
  const months = Math.floor(diffDays / 30);
  if (months === 1) return "pirms 1 mēneša";
  return `pirms ${months} mēnešiem`;
}
